<template>
  <AppCard elevation="0">
    <v-card-text>
      <div class="text-body-1 text-text-primary font-weight-medium mb-3">
        {{ $t('project.question-answer.QuestionAnswerItemAttachments.title') }}
      </div>

      <div v-if="attachedDocs.length === 0" class="text-body-1 text-text-secondary font-weight-regular">
        {{ $t('project.question-answer.QuestionAnswerItemAttachments.emptyState') }}
      </div>

      <div v-else class="qa-attachments__list" data-locator="qa-question-attachments-list">
        <div
          v-for="doc in attachedDocs"
          :key="doc.uniqueKey"
          class="qa-attachments__item cursor-pointer"
          data-locator="qa-question-attachment-item"
        >
          <div
            class="qa-attachments__item-main"
            data-locator="qa-question-attachment-open"
            @click="onOpenDocument(doc)"
          >
            <ClosdFildersIcon :document="doc" :size="28" />

            <div class="qa-attachments__content">
              <AppTooltip location="top" :data-locator="`qa-question-attachment-name-tooltip-${doc.uniqueKey}`">
                <template #activator="{ props }">
                  <div
                    v-bind="props"
                    class="text-body-1 text-text-secondary font-weight-regular text-truncate"
                    data-locator="qa-question-attachment-name"
                  >
                    <span v-if="doc.numbering" class="mr-1">{{ doc.numbering }}</span>
                    {{ doc.name || doc.basename }}
                  </div>
                </template>

                <span v-if="doc.numbering" class="mr-1">{{ doc.numbering }}</span>
                {{ doc.name || doc.basename }}
              </AppTooltip>

              <div
                class="text-caption text-text-tertiary font-weight-regular"
                data-locator="qa-question-attachment-meta"
              >
                <span v-if="doc.addedBy">{{ doc.addedBy }}</span>
                <template v-if="doc.addedBy && doc.addedAt">, </template>
                <DateDisplay v-if="doc.addedAt" :value="doc.addedAt" with-hours />
              </div>
            </div>
          </div>

          <QuestionAnswerItemAttachmentsMenu :document="doc" />
        </div>
      </div>
    </v-card-text>
  </AppCard>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import DateDisplay from '@/common/DateDisplay.vue'
import ClosdFildersIcon from '@/common/filders/ClosdFildersIcon.vue'
import { openFileViewer } from '@/common/utils/files'

import QuestionAnswerItemAttachmentsMenu from './QuestionAnswerItemAttachmentsMenu.vue'

export default {
  name: 'QuestionAnswerItemAttachments',
  components: {
    AppCard,
    AppTooltip,
    DateDisplay,
    ClosdFildersIcon,
    QuestionAnswerItemAttachmentsMenu,
  },
  computed: {
    ...mapState('questionAnswer', ['question']),
    ...mapGetters('room', ['roomMnemo']),
    attachedDocs () {
      const q = this.question || {}

      const questionFiles = (Array.isArray(q.files) ? q.files : [])
        .filter(Boolean)
        .map(file => ({
          ...file,
          type: 'file',
          name: file.name ?? file.basename,
          basename: file.basename ?? file.name,
          addedBy: q.userName || '',
          addedAt: q.createdAt || null,
          uniqueKey: `question-file-${file.id}`,
        }))

      const answerFiles = (Array.isArray(q.answers) ? q.answers : [])
        .flatMap(answer => {
          const files = Array.isArray(answer?.files) ? answer.files : []

          return files
            .filter(Boolean)
            .map(file => ({
              ...file,
              type: 'file',
              name: file.name ?? file.basename,
              basename: file.basename ?? file.name,
              addedBy: answer.userName || '',
              addedAt: answer.createdAt || null,
              uniqueKey: `answer-${answer.id}-file-${file.id}`,
            }))
        })

      return [...questionFiles, ...answerFiles]
    },
  },
  methods: {
    onOpenDocument (doc) {
      openFileViewer(this.roomMnemo, doc.id)
    },
  },
}
</script>

<style scoped lang="scss">
.qa-attachments__list {
  display: flex;
  flex-direction: column;
  max-height: 480px;
  overflow-y: auto;
  overflow-x: hidden;
}

.qa-attachments__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-radius: 12px;
  transition: background-color 0.2s ease;
}

.qa-attachments__item:hover {
  background: rgb(var(--v-theme-surface-secondary));
}

.qa-attachments__item:hover :deep(.qa-attachments__menu-btn) {
  background: rgb(var(--v-theme-surface-tertiary)) !important;
}

.qa-attachments__item-main {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.qa-attachments__content {
  min-width: 0;
  flex: 1 1 auto;
}
</style>
